import React, { useState, useEffect } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { Save } from 'lucide-react';
import { HitApi } from '../../Api/ApiHit';
import { addIncentiveProgram, searchIncentiveProgram, updateIncentiveProgram } from '../../constant/Constant';
import { getElevateUser, getSelectedUnit } from '../../storage/Storage';
import toast from 'react-hot-toast';
import DailyTargetIncentiveCard from './DailyTargetIncentiveCard';
import ProductIncentiveCard from './ProductIncentiveCard';
import MonthlyIncentiveCard from './MonthlyIncentiveCard';

function IncentiveSetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [existingProgram, setExistingProgram] = useState(null);
  const [formData, setFormData] = useState({
    dailyTragetIncentive: {
      type: 'multiple',
      of: 'salary',
      targetValue: 0,
      maxIncentive: 0,
      status: false
    },
    productIncentive: {
      tragetvalue: 0,
      type: 'multiple',
      of: 'sale',
      status: false
    },
    monthlyIncentive: {
      value: 0,
      type: 'multiple',
      of: 'salary',
      afterTargetPercentage: 0,
      status: false
    },
    status: 'inactive',
    freezeReason: '',
    notes: ''
  });

  // Search for existing incentive program
  const searchExistingProgram = async () => {
    setIsSearching(true);
    try {
      const selectedUnit = getSelectedUnit();
      
      if (!selectedUnit?._id) {
        setIsSearching(false);
        return;
      }

      const searchPayload = {
        page: 1,
        limit: 1,
        unitIds: [selectedUnit._id]
      };

      const response = await HitApi(searchPayload, searchIncentiveProgram);
      
      if (response?.statusCode === 200 && response?.data?.docs?.length > 0) {
        const program = response.data.docs[0];
        setExistingProgram(program);
        
        // Prefill form with existing data
        setFormData({
          dailyTragetIncentive: {
            type: program.dailyTragetIncentive?.type || 'multiple',
            of: program.dailyTragetIncentive?.of || 'salary',
            targetValue: program.dailyTragetIncentive?.targetValue || 0,
            maxIncentive: program.dailyTragetIncentive?.maxIncentive || 0,
            status: program.dailyTragetIncentive?.status || false
          },
          productIncentive: {
            tragetvalue: program.productIncentive?.tragetvalue || 0,
            type: program.productIncentive?.type || 'multiple',
            of: program.productIncentive?.of || 'sale',
            status: program.productIncentive?.status || false
          },
          monthlyIncentive: {
            value: program.monthlyIncentive?.value || 0,
            type: program.monthlyIncentive?.type || 'multiple',
            of: program.monthlyIncentive?.of || 'salary',
            afterTargetPercentage: program.monthlyIncentive?.afterTargetPercentage || 0,
            status: program.monthlyIncentive?.status || false
          },
          status: program.status || 'inactive',
          freezeReason: program.freezeReason || '',
          notes: program.notes || ''
        });
        
        toast.success('Existing incentive program loaded');
      } else {
        setExistingProgram(null);
      }
    } catch (error) {
      console.error('Error searching for existing program:', error);
      toast.error('Error checking for existing program');
    } finally {
      setIsSearching(false);
    }
  };

  // Load existing program on component mount
  useEffect(() => {
    searchExistingProgram();
  }, []);

  const handleInputChange = (category, field, value) => {
    if (category) {
      setFormData(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const userData = getElevateUser();
      const selectedUnit = getSelectedUnit();

      if (!userData?._id) {
        toast.error('User not found. Please login again.');
        return;
      }

      if (!selectedUnit?._id) {
        toast.error('Please select a unit first.');
        return;
      }

      let payload, response;
      
      if (existingProgram) {
        // Update existing program
        payload = {
          ...formData,
          _id: existingProgram._id
        };
        
        console.log('Updating incentive program:', payload);
        response = await HitApi(payload, updateIncentiveProgram);
      } else {
        // Create new program
        payload = {
          ...formData,
          createdBy: userData._id,
          unitIds: [selectedUnit._id]
        };
        
        console.log('Creating incentive program:', payload);
        response = await HitApi(payload, addIncentiveProgram);
      }

      if (response?.statusCode === 200 || response?.success) {
        toast.success(existingProgram ? 'Incentive program updated successfully!' : 'Incentive program created successfully!');
        
        // Only reset form for new creations, not for updates
        if (!existingProgram) {
          setFormData({
            dailyTragetIncentive: {
              type: 'multiple',
              of: 'salary',
              targetValue: 0,
              maxIncentive: 0,
              status: false
            },
            productIncentive: {
              tragetvalue: 0,
              type: 'multiple',
              of: 'sale',
              status: false
            },
            monthlyIncentive: {
              value: 0,
              type: 'multiple',
              of: 'salary',
              afterTargetPercentage: 0,
              status: false
            },
            status: 'inactive',
            freezeReason: '',
            notes: ''
          });
        } else {
          // For updates, refresh the existing program data
          if (response?.data) {
            setExistingProgram(response.data);
          }
        }
      } else {
        toast.error(response?.message || 'Failed to create incentive program');
      }
    } catch (error) {
      console.error('Error creating incentive program:', error);
      toast.error('Error creating incentive program. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const IncentiveCard = ({ title, icon: Icon, category, data }) => (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex items-center mb-4">
        <div className="p-2 bg-blue-100 rounded-lg mr-3">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>

      <div className="space-y-4">
        {/* Status Toggle */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Enable {title}</label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={data.status}
              onChange={(e) => handleInputChange(category, 'status', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Value Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {data.value !== undefined 
              ? `Target ${data.type.charAt(0).toUpperCase() + data.type.slice(1)} ${data.type === 'multiple' ? '(X)' : data.type === 'percentage' ? '(%)' : '(₹)'}` 
              : `Target ${data.type.charAt(0).toUpperCase() + data.type.slice(1)} ${data.type === 'multiple' ? '(X)' : data.type === 'percentage' ? '(%)' : '(₹)'}`}
          </label>
          <input
            type="number"
            min="0"
            value={data.value !== undefined ? data.value : data.tragetvalue}
            onChange={(e) => handleInputChange(category, data.value !== undefined ? 'value' : 'tragetvalue', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter value"
            disabled={!data.status}
          />
        </div>

        {/* Target Value (for dailyTragetIncentive and monthlyIncentive) */}
        {data.targetValue !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Value
            </label>
            <input
              type="number"
              min="0"
              value={data.targetValue}
              onChange={(e) => handleInputChange(category, 'targetValue', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter target value"
              disabled={!data.status}
            />
          </div>
        )}

        {/* Target Value Type (for monthlyIncentive only) */}
        {data.targetValueType !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Value Type
            </label>
            <select
              value={data.targetValueType}
              onChange={(e) => handleInputChange(category, 'targetValueType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!data.status}
            >
              <option value="percentage">Percentage</option>
              <option value="amount">Amount</option>
            </select>
          </div>
        )}

        {/* Max Incentive (for dailyTragetIncentive and monthlyIncentive) */}
        {data.maxIncentive !== undefined && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Incentive (₹)
            </label>
            <input
              type="number"
              min="0"
              value={data.maxIncentive}
              onChange={(e) => handleInputChange(category, 'maxIncentive', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter max incentive"
              disabled={!data.status}
            />
          </div>
        )}

        {/* Incentive Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Incentive Type
          </label>
          <select
            value={data.type}
            onChange={(e) => handleInputChange(category, 'type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!data.status}
          >
            <option value="multiple">Multiple</option>
            <option value="fixed">Fixed</option>
            <option value="percentage">Percentage</option>
          </select>
        </div>

        {/* Multiple Of */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Multiple Of
          </label>
          <select
            value={data.of}
            onChange={(e) => handleInputChange(category, 'of', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!data.status}
          >
            <option value="salary">Salary</option>
            <option value="sale">Sale</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-5">
      <PageHeader
        title={existingProgram ? "Edit Incentive Settings" : "Incentive Settings"}
        description={existingProgram ? "Update existing incentive program for your team" : "Set up incentive programs to motivate your team"}
      />
      
      {isSearching && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">Checking for existing incentive program...</p>
        </div>
      )}
      
      {existingProgram && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Existing Program Found:</strong> Editing incentive program created on {new Date(existingProgram.createdAt).toLocaleDateString()}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Incentive Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DailyTargetIncentiveCard
            data={formData.dailyTragetIncentive}
            handleInputChange={handleInputChange}
          />
          
          <ProductIncentiveCard
            data={formData.productIncentive}
            handleInputChange={handleInputChange}
          />
          
          <MonthlyIncentiveCard
            data={formData.monthlyIncentive}
            handleInputChange={handleInputChange}
          />

          {/* Program Settings */}
          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-purple-100 rounded-lg mr-3">
                <Save className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Program Settings</h3>
            </div>
            
            <div className="space-y-4">
              {/* Program Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange(null, 'status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="inactive">Inactive</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Freeze Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Freeze Reason (Optional)
                </label>
                <input
                  type="text"
                  value={formData.freezeReason}
                  onChange={(e) => handleInputChange(null, 'freezeReason', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Reason for freezing program"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange(null, 'notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Additional notes about this incentive program..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>{existingProgram ? 'Update Incentive Program' : 'Create Incentive Program'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default IncentiveSetting;